<!-- (c) TwistedMinds, Gerry Uytdenhouwen - 3 november 2026 -->
<template>
    <div class="period-fields" :class="`period-fields--${variant}`">
        <v-tooltip bottom>
            <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                <div class="period-fields__tip" v-bind="tooltipAttrs" v-on="tooltipOn">
                <v-menu
                    v-model="dataRange"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    :return-value="dateRangeText"
                    transition="scale-transition"
                    offset-y
                    style="color: #FFFFFF"
                    max-width="290px"
                    min-width="290px"
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                            :class="fieldClass"
                            v-model="dateRangeText"
                            :label="$t('general.dates')"
                            readonly
                            v-bind="fieldBind('mdi-calendar', attrs)"
                            v-on="on"
                            :rules="[(!dates[0] || !!dates[1]) || $t('general.required-end-date')]"
                        ></v-text-field>
                    </template>
                    <v-date-picker v-if="dataRange" range v-model="datesModel" @change="onChange"></v-date-picker>
                </v-menu>
                </div>
            </template>
            <span>{{ $t('general.tt_filter_date') }}</span>
        </v-tooltip>
        <v-tooltip bottom>
            <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                <div class="period-fields__tip" v-bind="tooltipAttrs" v-on="tooltipOn">
                <v-menu
                    v-model="timeRange"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    :return-value="timeRangeText"
                    offset-y
                    transition="scale-transition"
                    style="color: #FFFFFF"
                    max-width="290px"
                    min-width="290px"
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                            :class="fieldClass"
                            v-model="timeRangeText"
                            :label="$t('general.times')"
                            readonly
                            v-bind="fieldBind('mdi-clock-outline', attrs)"
                            v-on="on"
                            :rules="[(!times[0] || !!times[1]) || $t('general.required-end-time')]"
                        ></v-text-field>
                    </template>
                    <v-row
                        justify="space-around"
                        align="center"
                    >
                        <h3>Start:</h3>
                        <v-time-picker
                            v-model="startTimeModel"
                            :max="times[1]"
                            format="24hr"
                            @click:hour="onChange"
                            @click:minute="onChange"
                        ></v-time-picker>
                        <h3>End:</h3>
                        <v-time-picker
                            v-model="endTimeModel"
                            :min="times[0]"
                            format="24hr"
                            @click:hour="onChange"
                            @click:minute="onChange"
                        ></v-time-picker>
                    </v-row>
                </v-menu>
                </div>
            </template>
            <span>{{ $t('general.tt_filter_time') }}</span>
        </v-tooltip>
    </div>
</template>

<script>
export default {
    name: "OrderPeriodFields",
    props: {
        dates: {type: Array, default: () => []},
        times: {type: Array, default: () => []},
        // classic = today's fields in the old row, soft = fields for the filter card
        variant: {type: String, default: 'classic', validator: (value) => ['classic', 'soft'].includes(value)},
    },
    data: () => ({
        dataRange: false,
        timeRange: false,
    }),
    computed: {
        isSoft() {
            return this.variant === 'soft';
        },
        fieldClass() {
            return this.isSoft ? 'period-fields__field' : 'mx-2';
        },
        dateRangeText() {
            let dateStart = this.formatDate(this.dates[0]);
            let dateEnd = this.formatDate(this.dates[1]);
            if (dateStart && dateEnd) {
                return dateStart + " ~ " + dateEnd
            }
            return dateStart;
        },
        timeRangeText() {
            let timeStart = this.times[0];
            let timeEnd = this.times[1];
            if (timeStart && timeEnd) {
                return timeStart + " ~ " + timeEnd;
            }
            return timeStart;
        },
        datesModel: {
            get() {
                return this.dates;
            },
            set(value) {
                this.$emit('update:dates', value);
            },
        },
        startTimeModel: {
            get() {
                return this.times[0];
            },
            set(value) {
                this.setTime(0, value);
            },
        },
        endTimeModel: {
            get() {
                return this.times[1];
            },
            set(value) {
                this.setTime(1, value);
            },
        },
    },
    methods: {
        formatDate(date) {
            if (!date) return null;
            const parts = date.split('-');
            return `${parts[2]}-${parts[1]}-${parts[0]}`;
        },
        // soft adds the v2 field look and the teal icon; classic leaves the field untouched
        fieldBind(icon, attrs) {
            if (!this.isSoft) return attrs;
            return {
                ...attrs,
                solo: true,
                flat: true,
                // no empty message row under the field; the end-date/time error still shows when it applies
                'hide-details': 'auto',
                'background-color': '#f2f4f5',
                'prepend-inner-icon': icon,
            };
        },
        setTime(index, value) {
            const next = [...this.times];
            next[index] = value;
            this.$emit('update:times', next);
        },
        onChange() {
            this.$emit('change');
        },
    },
}
</script>

<style scoped lang="scss">
.period-fields {
    // classic: the two fields sit directly in the parent row, exactly as before
    &--classic {
        display: contents;
    }

    .period-fields__tip {
        flex: 1 1 auto;
    }

    // soft: v2 look for the filter card
    &--soft {
        display: flex;
        flex: 0 1 auto;
        flex-wrap: wrap;
        gap: 12px;

        // fixed width, wide enough for a full range like "29-10-2026 ~ 30-10-2026"; shrinks on narrow screens
        .period-fields__tip {
            flex: 0 1 260px;
            width: 260px;
            min-width: 130px;
        }

        .period-fields__field {
            margin: 0;
        }

        // the grey box and its Vuetify wrapper are both 40px, so the field lines up with the period buttons
        ::v-deep .v-input__control,
        ::v-deep .v-input__slot {
            min-height: 40px;
        }

        ::v-deep .v-input__slot {
            border-radius: 9px;
            box-shadow: none;
            margin-bottom: 0;
        }

        ::v-deep .v-input__icon .v-icon {
            color: #1d6f86;
        }
    }
}
</style>
